import React, { useState, useEffect, useCallback, useRef } from 'react';
import SplitMap from './components/SplitMap';
import SearchBar from './components/SearchBar';
import MapStyleButton from './components/MapStyleButton';
import MapStyleDialog, { mapStyles, noneMapStyle, MapStyle } from './components/MapStyleDialog';
import ZoomDisplay from './components/ZoomDisplay';
import OsmLinkButton from './components/OsmLinkButton';
import AmericanaLinkButton from './components/AmericanaLinkButton';

interface SearchResult {
  lat: number;
  lon: number;
  zoom?: number;
  display_name: string;
}

function App() {
  const [selectedLocation, setSelectedLocation] = useState<SearchResult | undefined>();
  const [isStyleDialogOpen, setIsStyleDialogOpen] = useState(false);
  const [currentZoom, setCurrentZoom] = useState<number>(10);
  const [isUpdatingHash, setIsUpdatingHash] = useState(false);
  
  // Cache for parsed custom styles to prevent object recreation
  const customStyleCacheRef = useRef<Record<string, MapStyle>>({});

  const osmLinkButtonRef = useRef<HTMLButtonElement>(null);
  const americanaLinkButtonRef = useRef<HTMLButtonElement>(null);

  const parseHashParams = useCallback(() => {
    const hash = window.location.hash.slice(1);
    const [locationPart, queryPart] = hash.split('?');
    
    let zoom = 10;
    if (locationPart) {
      const parts = locationPart.split('/');
      if (parts.length === 3) {
        zoom = parseInt(parts[0], 10) || 10;
      }
    }
    
    const params = new URLSearchParams(queryPart || '');

    // Parse left style (predefined or custom)
    let leftStyle = mapStyles[0];
    const leftStyleCustom = params.get('leftStyleCustom');
    if (leftStyleCustom) {
      // Check cache first to avoid recreating the same object
      if (customStyleCacheRef.current[leftStyleCustom]) {
        leftStyle = customStyleCacheRef.current[leftStyleCustom];
      } else {
        try {
          leftStyle = JSON.parse(leftStyleCustom);
          customStyleCacheRef.current[leftStyleCustom] = leftStyle;
        } catch (e) {
          console.error('Failed to parse custom left style:', e);
        }
      }
    } else {
      const leftStyleIndex = parseInt(params.get('leftStyle') || '0', 10);
      leftStyle = mapStyles[leftStyleIndex] || mapStyles[0];
    }
    
    // Parse right style (predefined or custom)
    let rightStyle = mapStyles[0];
    const rightStyleCustom = params.get('rightStyleCustom');
    if (rightStyleCustom) {
      // Check cache first to avoid recreating the same object
      if (customStyleCacheRef.current[rightStyleCustom]) {
        rightStyle = customStyleCacheRef.current[rightStyleCustom];
      } else {
        try {
          rightStyle = JSON.parse(rightStyleCustom);
          customStyleCacheRef.current[rightStyleCustom] = rightStyle;
        } catch (e) {
          console.error('Failed to parse custom right style:', e);
        }
      }
    } else {
      const rightStyleIndex = parseInt(params.get('rightStyle') || '0', 10);
      rightStyle = mapStyles[rightStyleIndex] || mapStyles[0];
    }

    // Parse right base layer style (predefined or custom)
    let rightBaseStyle = mapStyles[0];
    const rightBaseStyleCustom = params.get('rightBaseStyleCustom');
    if (rightBaseStyleCustom) {
      // Check cache first to avoid recreating the same object
      if (customStyleCacheRef.current[rightBaseStyleCustom]) {
        rightBaseStyle = customStyleCacheRef.current[rightBaseStyleCustom];
      } else {
        try {
          rightBaseStyle = JSON.parse(rightBaseStyleCustom);
          customStyleCacheRef.current[rightBaseStyleCustom] = rightBaseStyle;
        } catch (e) {
          console.error('Failed to parse custom right base style:', e);
        }
      }
    } else {
      const rightBaseStyleIndex = parseInt(params.get('rightBaseStyle') || '0', 10);
      rightBaseStyle = mapStyles[rightBaseStyleIndex] || mapStyles[0];
    }

    // Parse right third layer style (predefined or custom); absent means no third layer
    let rightThirdStyle = noneMapStyle;
    const rightThirdStyleCustom = params.get('rightThirdStyleCustom');
    if (rightThirdStyleCustom) {
      // Check cache first to avoid recreating the same object
      if (customStyleCacheRef.current[rightThirdStyleCustom]) {
        rightThirdStyle = customStyleCacheRef.current[rightThirdStyleCustom];
      } else {
        try {
          rightThirdStyle = JSON.parse(rightThirdStyleCustom);
          customStyleCacheRef.current[rightThirdStyleCustom] = rightThirdStyle;
        } catch (e) {
          console.error('Failed to parse custom right third style:', e);
        }
      }
    } else {
      const rightThirdStyleIndex = parseInt(params.get('rightThirdStyle') || '', 10);
      rightThirdStyle = mapStyles[rightThirdStyleIndex] || noneMapStyle;
    }

    return {
      leftStyle,
      rightStyle,
      rightBaseStyle,
      rightThirdStyle,
      zoom
    };
  }, []);

  const initialState = parseHashParams();
  const [leftMapStyle, setLeftMapStyle] = useState<MapStyle>(initialState.leftStyle);
  const [rightMapStyle, setRightMapStyle] = useState<MapStyle>(initialState.rightStyle);
  const [rightBaseMapStyle, setRightBaseMapStyle] = useState<MapStyle>(initialState.rightBaseStyle);
  const [rightThirdMapStyle, setRightThirdMapStyle] = useState<MapStyle>(initialState.rightThirdStyle);

  useEffect(() => {
    setCurrentZoom(initialState.zoom);
  }, [initialState.zoom]);

  const updateHash = useCallback(() => {
    setIsUpdatingHash(true);
    const hash = window.location.hash.slice(1);
    const [locationPart] = hash.split('?');
    
    const params = new URLSearchParams();

    // Handle predefined styles vs custom styles
    const leftStyleIndex = mapStyles.findIndex(s => s.name === leftMapStyle.name);
    if (leftStyleIndex >= 0) {
      params.set('leftStyle', leftStyleIndex.toString());
    } else {
      // Custom style - encode as JSON
      params.set('leftStyleCustom', JSON.stringify(leftMapStyle));
    }
    
    const rightStyleIndex = mapStyles.findIndex(s => s.name === rightMapStyle.name);
    if (rightStyleIndex >= 0) {
      params.set('rightStyle', rightStyleIndex.toString());
    } else {
      // Custom style - encode as JSON
      params.set('rightStyleCustom', JSON.stringify(rightMapStyle));
    }

    const rightBaseStyleIndex = mapStyles.findIndex(s => s.name === rightBaseMapStyle.name);
    if (rightBaseStyleIndex >= 0) {
      params.set('rightBaseStyle', rightBaseStyleIndex.toString());
    } else {
      // Custom style - encode as JSON
      params.set('rightBaseStyleCustom', JSON.stringify(rightBaseMapStyle));
    }

    if (rightThirdMapStyle.name !== noneMapStyle.name) {
      const rightThirdStyleIndex = mapStyles.findIndex(s => s.name === rightThirdMapStyle.name);
      if (rightThirdStyleIndex >= 0) {
        params.set('rightThirdStyle', rightThirdStyleIndex.toString());
      } else {
        // Custom style - encode as JSON
        params.set('rightThirdStyleCustom', JSON.stringify(rightThirdMapStyle));
      }
    }

    const newHash = locationPart ? `${locationPart}?${params.toString()}` : `?${params.toString()}`;
    window.location.hash = newHash;
    setTimeout(() => setIsUpdatingHash(false), 0);
  }, [leftMapStyle, rightMapStyle, rightBaseMapStyle, rightThirdMapStyle]);

  useEffect(() => {
    updateHash();
  }, [updateHash]);

  useEffect(() => {
    const handleHashChange = () => {
      if (isUpdatingHash) return;
      
      const newState = parseHashParams();
      setLeftMapStyle(newState.leftStyle);
      setRightMapStyle(newState.rightStyle);
      setRightBaseMapStyle(newState.rightBaseStyle);
      setRightThirdMapStyle(newState.rightThirdStyle);
      setCurrentZoom(newState.zoom);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [parseHashParams, isUpdatingHash]);

  // Keyboard shortcuts: "1" opens OSM.org, "2" opens americanamap.org, at the
  // current location, same as clicking the corresponding link buttons.
  // "3" opens the Map Styles panel.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const active = document.activeElement;
      const isEditable =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active as HTMLElement | null)?.isContentEditable;
      if (isEditable) return;

      if (e.key === '1') {
        osmLinkButtonRef.current?.click();
      } else if (e.key === '2') {
        americanaLinkButtonRef.current?.click();
      } else if (e.key === '3') {
        setIsStyleDialogOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelectLocation = (result: SearchResult) => {
    setSelectedLocation(result);
  };

  const handleOpenStyleDialog = () => {
    setIsStyleDialogOpen(true);
  };

  const handleCloseStyleDialog = () => {
    setIsStyleDialogOpen(false);
  };

  return (
    <div style={{ position: 'relative', height: '100vh', width: '100vw' }}>
      <SplitMap
        searchResult={selectedLocation}
        leftMapStyle={leftMapStyle}
        rightMapStyle={rightMapStyle}
        rightBaseMapStyle={rightBaseMapStyle}
        rightThirdMapStyle={rightThirdMapStyle}
      />
      <SearchBar onSelectLocation={handleSelectLocation} />
      <ZoomDisplay zoom={currentZoom} />
      <OsmLinkButton ref={osmLinkButtonRef} />
      <AmericanaLinkButton ref={americanaLinkButtonRef} />
      <MapStyleButton onClick={handleOpenStyleDialog} />
      <MapStyleDialog
        isOpen={isStyleDialogOpen}
        onClose={handleCloseStyleDialog}
        leftMapStyle={leftMapStyle}
        rightMapStyle={rightMapStyle}
        rightBaseMapStyle={rightBaseMapStyle}
        rightThirdMapStyle={rightThirdMapStyle}
        onLeftStyleChange={setLeftMapStyle}
        onRightStyleChange={setRightMapStyle}
        onRightBaseStyleChange={setRightBaseMapStyle}
        onRightThirdStyleChange={setRightThirdMapStyle}
      />
    </div>
  );
}

export default App;
